import {
  Expressions,
  matchExpression,
  Plugins,
  type Component,
} from '@packages/systems/wfdl';
import {
  getName,
  getPluginName,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {isStringElement} from '@packages/utilities/elementTypeUtils';
import {
  defaultButtonChildren,
  defaultTextLinkChildren,
  defaultHeadingChildren,
  defaultParagraphChildren,
  defaultTextDivChildren,
  defaultBlockquoteChildren,
  defaultRichTextChildren,
} from '@packages/systems/core/constants/DomNodeDefaults';

type QualifiedType = Qualified<Component.ComponentName>;

/**
 * Standalone default constants (e.g. `defaultHeadingChildren`) are a bare
 * String element, not wrapped in an Element of their own type — the type has
 * to be seeded from the constant itself.
 */
const SEEDED_DEFAULTS: Array<[string, Array<Expression>]> = [
  // Button and Text Link are both Basic/Link at runtime (they differ only by
  // the `button` flag), so their defaults share one entry.
  ['Link', [...defaultTextLinkChildren, ...defaultButtonChildren]],
  ['Heading', defaultHeadingChildren],
  ['Paragraph', defaultParagraphChildren],
  ['Block', defaultTextDivChildren],
  ['Blockquote', defaultBlockquoteChildren],
];

/**
 * Rich Text is never itself checked against a default (it's a container) —
 * its nested elements (Heading, Paragraph, Blockquote, Link, ...) are walked
 * and checked individually like any other page element, but they carry their
 * own distinct default strings ("Heading 1", not "Heading"; a different lorem
 * ipsum; "Block quote" not "Block Quote"), so those need seeding too.
 *
 * Only the in-scope types above are recorded (enforced in `collectDefaultTexts`
 * via `IN_SCOPE_TYPE_NAMES`) — Rich Text also nests List/ListItem and inline
 * formatting (Strong/Emphasized/Superscript/Subscript), whose defaults are
 * generic words/numbering ("Item 1", "Bold text") real content plausibly
 * matches, so those stay excluded.
 */
const IN_SCOPE_TYPE_NAMES = new Set(
  SEEDED_DEFAULTS.map(([typeName]) => typeName)
);

/**
 * The default Rich Text's list-intro paragraphs ("Ordered list",
 * "Unordered list") are generic enough that a standalone page paragraph
 * plausibly matches them for real — same collision class as List/ListItem
 * and Strong, so excluded from the Paragraph default set even though
 * Paragraph itself is in scope.
 */
export const EXCLUDED_TEXTS: ReadonlySet<string> = new Set([
  'Ordered list',
  'Unordered list',
]);

function collectDefaultTexts(
  expr: Expression,
  currentTypeName: string,
  out: Map<string, Set<string>>
): void {
  matchExpression({
    Element: (el) => {
      // The `String` element is just a text-leaf wrapper (see `createTextElement`
      // in DomNodeDefaults) — it never contributes its own type to the map.
      collectDefaultTexts(
        el.data,
        isStringElement(el) ? currentTypeName : getName(el.type),
        out
      );
    },
    Text: (text) => {
      if (
        !text ||
        !IN_SCOPE_TYPE_NAMES.has(currentTypeName) ||
        EXCLUDED_TEXTS.has(text)
      ) {
        return;
      }
      if (!out.has(currentTypeName)) {
        out.set(currentTypeName, new Set());
      }
      out.get(currentTypeName)!.add(text);
    },
    Record: (record) => {
      for (const field of Object.values(record)) {
        collectDefaultTexts(field, currentTypeName, out);
      }
    },
    List: (items) => {
      for (const item of items) {
        collectDefaultTexts(item, currentTypeName, out);
      }
    },
    _: () => {},
  })(expr);
}

function buildDefaultTextMap(): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();

  for (const [typeName, expressions] of SEEDED_DEFAULTS) {
    for (const expr of expressions) {
      collectDefaultTexts(expr, typeName, map);
    }
  }

  for (const child of defaultRichTextChildren) {
    collectDefaultTexts(child, '', map);
  }

  return map;
}

let cache: Map<string, Set<string>> | undefined;

/**
 * Whether `text` is exactly the placeholder content Webflow inserts when
 * creating a new element of `type` (e.g. a Heading's "Heading") — not
 * something the user actually typed.
 */
export function isDefaultElementText(
  type: QualifiedType,
  text: string
): boolean {
  if (getPluginName(type) !== Plugins.name('Basic')) return false;
  if (!cache) {
    cache = buildDefaultTextMap();
  }
  return cache.get(getName(type))?.has(text) ?? false;
}

/**
 * All text leaves in visitation order, ignoring ids/types — used by the
 * `EXCLUDED_TEXTS` drift test to confirm those strings still appear in the
 * real Rich Text default content.
 */
export function collectAllText(
  expr: Expression,
  out: Array<string> = []
): string[] {
  matchExpression({
    Element: (el) => collectAllText(el.data, out),
    Text: (text) => {
      out.push(text);
    },
    Record: (record) => {
      for (const field of Object.values(record)) {
        collectAllText(field, out);
      }
    },
    List: (items) => {
      for (const item of items) {
        collectAllText(item, out);
      }
    },
    _: () => {},
  })(expr);
  return out;
}

/**
 * Text leaves of `String` elements only, in visitation order — unlike
 * `collectAllText`, this ignores non-user-visible structural text fields
 * (e.g. List/ListItem's `list.type`, a Text Link's `block`), so it only
 * reflects content a user could actually see or edit.
 */
function collectStringElementText(
  expr: Expression,
  out: Array<string> = []
): string[] {
  matchExpression({
    Element: (el) => {
      if (isStringElement(el)) {
        const text = Expressions.getText(el.data);
        if (text) out.push(text);
        return;
      }
      collectStringElementText(el.data, out);
    },
    Record: (record) => {
      for (const field of Object.values(record)) {
        collectStringElementText(field, out);
      }
    },
    List: (items) => {
      for (const item of items) {
        collectStringElementText(item, out);
      }
    },
    _: () => {},
  })(expr);
  return out;
}

let defaultRichTextLeaves: string[] | undefined;

/**
 * A Rich Text element is never translated as a whole — its nested elements
 * are walked and checked individually. But when the whole thing is still
 * exactly the pristine default content Webflow inserts (nothing edited yet),
 * there is nothing real to translate anywhere in it, including the
 * List/ListItem and inline-formatting (Strong/Emphasized/...) content that
 * `isDefaultElementText` deliberately doesn't cover on its own.
 */
export function isDefaultRichTextContent(element: ExpressionElement): boolean {
  if (!defaultRichTextLeaves) {
    defaultRichTextLeaves = collectStringElementText(
      EList(defaultRichTextChildren)
    );
  }
  const children = Expressions.getRecord(element.data)?.children;
  if (!children) return false;
  const actualLeaves = collectStringElementText(children);
  return (
    actualLeaves.length === defaultRichTextLeaves.length &&
    actualLeaves.every((text, i) => text === defaultRichTextLeaves![i])
  );
}
